/**
 * Phase D: Forensic upload audit logger.
 *
 * Records every upload lifecycle event into `public.file_upload_audit`
 * for admin visibility, debugging, and compliance. Best-effort: failures
 * are logged but never block the upload pipeline.
 */

import { supabase } from "@/integrations/supabase/client";
import type { QueuedUpload } from "./queue";

export type UploadAuditEvent =
  | "queued"
  | "init"
  | "started"
  | "paused"
  | "resumed"
  | "completed"
  | "failed"
  | "cancelled"
  | "duplicate_detected"
  | "aborted";

export interface UploadAuditPayload {
  userId: string;
  upload: Pick<
    QueuedUpload,
    | "localUploadId"
    | "fileName"
    | "fileSize"
    | "mimeType"
    | "folderId"
    | "sessionId"
    | "r2UploadId"
    | "r2FileKey"
    | "sha256"
  >;
  event: UploadAuditEvent;
  data?: Record<string, unknown>;
  errorMessage?: string;
}

export async function logUploadAudit(payload: UploadAuditPayload): Promise<void> {
  try {
    const { upload, event, data, errorMessage, userId } = payload;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
    await supabase.from("file_upload_audit").insert([{
      user_id: userId,
      local_upload_id: upload.localUploadId,
      session_id: upload.sessionId ?? null,
      r2_upload_id: upload.r2UploadId ?? null,
      r2_file_key: upload.r2FileKey ?? null,
      file_name: upload.fileName,
      file_size: upload.fileSize,
      mime_type: upload.mimeType ?? null,
      folder_id: upload.folderId ?? null,
      sha256: upload.sha256 ?? null,
      event_type: event,
      event_data: (data ?? {}) as any,
      error_message: errorMessage ?? null,
      user_agent: ua,
    }]);
  } catch (e) {
    // Never let audit failure block uploads
    console.warn("[upload-audit] insert failed:", e);
  }
}
